import extractQueryParamFromPageLocation from '@src/quince-site-common/src/utils/extractQueryParamFromPageLocation'
import getCookie from '@src/quince-site-common/src/utils/getCookie'

/**
 * Reddit tracking constants
 * Based on Reddit CAPI documentation
 */
export const REDDIT_TRACKING_CONSTANTS = {
  // Reddit adds rdt_cid to destination URL, then sets cookies
  CLICK_ID_URL_PARAM: 'rdt_cid',
  CLICK_ID_COOKIE: '_rdt_cid',
  UUID_COOKIE: '_rdt_uuid',
  // Alternative cookie names Reddit might use
  ALTERNATIVE_CLICK_ID_COOKIES: ['rdt_cid', 'reddit_click_id', 'click_id'],
} as const

/**
 * Extracts query parameter from referrer URL
 */
const extractQueryParamFromReferrer = (key: string): string => {
  try {
    if (typeof window === 'undefined' || !window.document.referrer) {
      return ''
    }

    const url = new URL(window.document.referrer)
    return url.searchParams.get(key) || ''
  } catch (error) {
    return ''
  }
}

/**
 * Extracts Reddit Click ID with proper priority
 * Priority: URL Parameter > Cookie > Referrer
 *
 * Reddit flow: URL parameter → Cookie → Our extraction
 */
export const getRedditClickId = (): string => {
  // 1. First try URL parameter (Reddit adds rdt_cid to destination URL)
  const urlClickId = extractQueryParamFromPageLocation(REDDIT_TRACKING_CONSTANTS.CLICK_ID_URL_PARAM)
  if (urlClickId) {
    return urlClickId
  }

  // 2. Try primary Reddit cookie
  let clickId = getCookie(REDDIT_TRACKING_CONSTANTS.CLICK_ID_COOKIE)
  if (clickId) {
    return clickId
  }

  // 3. Try alternative cookie names
  for (const cookieName of REDDIT_TRACKING_CONSTANTS.ALTERNATIVE_CLICK_ID_COOKIES) {
    clickId = getCookie(cookieName)
    if (clickId) {
      return clickId
    }
  }

  // 4. Fallback to referrer (if available)
  const referrerClickId = extractQueryParamFromReferrer(REDDIT_TRACKING_CONSTANTS.CLICK_ID_URL_PARAM)
  return referrerClickId
}

/**
 * Extracts Reddit UUID from cookie
 */
export const getRedditUuid = (): string => {
  const uuid = getCookie(REDDIT_TRACKING_CONSTANTS.UUID_COOKIE)
  return uuid ?? ''
}

/**
 * Gets both Reddit Click ID and UUID as an object
 */
export const getRedditTrackingData = (): {
  clickId: string
  uuid: string
} => {
  return {
    clickId: getRedditClickId(),
    uuid: getRedditUuid(),
  }
}
